/**
 * AddInstance — the EIA "Add instance" card on Manage Portfolio: describe one
 * instance (where it runs, what it is, how hard it works) and add it to the
 * portfolio.
 *
 * Ported from legacy ManageAccount.vue (EIA fields :444-541, addInstanceClick
 * :612-758). The rules, field keys and the row sent on Add live in
 * ../portfolioFormRules.js so they are tested without rendering.
 *
 * The form itself is local state — it is a draft until "Add instance". The
 * option lists are NOT: Region and Instance Type come from the server, so the
 * page passes them in (with their loading flags) and is told when the region
 * changes, because the instance types depend on it.
 *
 * Kept from legacy: changing Region clears Instance Type (and OCI flex OCPU /
 * Memory), since the old choice may not exist in the new region; the form
 * resets after a successful add; OCPU + Memory appear only for an OCI flex shape.
 *
 * Project rule note (.claude/rules/variants.md): prefer one shared component
 * with EIA-only parts behind <VariantGate only="eia"> over per-product folders.
 * If this grows a CCA twin, merge the two instead.
 */
import { useState } from 'react';

import { Dropdown } from '@components/common/Dropdown';
import { FloatingTooltip } from '@components/common/FloatingTooltip';
import { FindReplaceIcon, InfoIcon, PlusIcon } from '@components/common/icons';
import { NumberField } from '@components/common/NumberField';
import { TextField } from '@components/common/TextField';
import { Button, Card, CardBody, Tooltip } from '@components/ui';

import {
  FIELD,
  OS_OPTIONS,
  PRICING_MODEL_OPTIONS,
  buildEiaInstance,
  emptyInstanceForm,
  isOciFlex,
  validateInstanceForm,
} from '../portfolioFormRules';

import styles from './AddInstance.module.css';

/** The "Consumption data" number fields, in the design's order. */
const CONSUMPTION_FIELDS = [
  { id: FIELD.MAX_MEM, label: 'Max Mem', placeholder: 'Enter max mem', required: true },
  { id: FIELD.MAX_CPU, label: 'Max CPU %', placeholder: 'Enter max CPU %', required: true },
  {
    id: FIELD.MAX_NETWORK_BW,
    label: 'Max Network BW',
    placeholder: 'Enter max network BW',
    required: true,
  },
  { id: FIELD.MAX_DISK_BW, label: 'Max Disk BW', placeholder: 'Enter max disk BW', required: true },
  {
    id: FIELD.MAX_IOPS,
    label: 'Max IOPS',
    placeholder: 'Integers only',
    required: true,
    integer: true,
  },
  { id: FIELD.UAVG, label: 'UAVG', placeholder: 'Enter UAVG' },
  { id: FIELD.U95, label: 'U95', placeholder: 'Enter U95' },
];

/**
 * Each field's hover hint — legacy's EIA tooltips, verbatim
 * (ManageAccount.vue fieldsForEIA :444-541). Pricing Model has none in legacy.
 */
const FIELD_TIPS = Object.freeze({
  [FIELD.REGION]: 'Select Region associated with CSP',
  [FIELD.INSTANCE_TYPE]: 'Instances associated with Region.',
  [FIELD.OS]: 'Select your Operating System',
  [FIELD.UUID]: 'UUID/Instance Name. For Ex: VM for AI/ML server.',
  [FIELD.OCPU]: 'Number of OCPUs for the OCI flex instance.',
  [FIELD.MEMORY]: 'Memory in GB for the OCI flex instance.',
  [FIELD.MAX_CPU]: 'Maximum CPU utilization percentage',
  [FIELD.MAX_MEM]: 'Maximum memory utilization',
  [FIELD.MAX_NETWORK_BW]: 'Maximum network bandwidth',
  [FIELD.MAX_DISK_BW]: 'Maximum disk bandwidth',
  [FIELD.MAX_IOPS]: 'Maximum Input/Output operations per second',
  [FIELD.UAVG]: 'UAVG',
  [FIELD.U95]: 'U95',
});

/**
 * A field with its hover hint. Floating (drawn over the page) so a hint near
 * the right edge can never push out a sideways scrollbar; hover only, so it
 * never covers the field being typed in (legacy hides them while focused).
 */
function FieldTip({ field, children }) {
  const tip = FIELD_TIPS[field];
  if (!tip) return children;
  return (
    <FloatingTooltip label={tip} side="bottom" hoverOnly className={styles.fieldTip}>
      {children}
    </FloatingTooltip>
  );
}

/** Legacy's tooltip on the help button, also its accessible name. */
const HELP_LABEL = 'Data correction & adjustment guidelines';

/** DOM ids must not contain spaces or '%'; the API keys do. */
const domId = (field) => `add-instance-${field.replace(/[^a-z0-9]+/gi, '-')}`;

export function AddInstance({
  provider,
  regions = [],
  regionsLoading = false,
  regionsError,
  instanceTypes = [],
  instanceTypesLoading = false,
  instanceTypesError,
  onRegionChange,
  onAdd,
  onHelp,
  /** Opens Find and Replace; no button without it (permission-gated by the page). */
  onFindReplace,
  /** Why Find and Replace can't run right now (shown as its title), or null. */
  findReplaceBlockedBy = null,
  disabled = false,
}) {
  const [form, setForm] = useState(emptyInstanceForm);
  const [errors, setErrors] = useState({});
  // While the page has the row checked by the server (/input/validate).
  const [adding, setAdding] = useState(false);

  const flex = isOciFlex(provider, form[FIELD.INSTANCE_TYPE]);

  /** Update one field and drop its error — the user is fixing it. */
  const set = (field, value, extra = {}) => {
    setForm((f) => ({ ...f, [field]: value, ...extra }));
    setErrors((e) => {
      if (!e[field]) return e;
      const { [field]: _gone, ...rest } = e;
      return rest;
    });
  };

  const changeRegion = (region) => {
    set(FIELD.REGION, region, {
      [FIELD.INSTANCE_TYPE]: '',
      [FIELD.OCPU]: '',
      [FIELD.MEMORY]: '',
    });
    onRegionChange?.(region);
  };

  /**
   * `onAdd` may be async and returns false when the row was NOT added (the
   * server refused it): then the form keeps its values so the user can fix
   * and retry, instead of retyping everything.
   */
  const add = async () => {
    const found = validateInstanceForm(form, provider);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setAdding(true);
    try {
      const added = await onAdd?.(buildEiaInstance(form, provider));
      if (added === false) return;
      setForm(emptyInstanceForm());
      // The form's region is cleared — tell the page, so the instance-type
      // list it holds follows.
      onRegionChange?.('');
    } finally {
      setAdding(false);
    }
  };

  const numberField = ({ id, label, placeholder, required, integer }) => (
    <FieldTip key={id} field={id}>
      <NumberField
        id={domId(id)}
        label={label}
        required={required}
        placeholder={placeholder}
        value={form[id]}
        onChange={(e) => set(id, e.target.value)}
        min={0}
        integer={integer}
        disabled={disabled}
        error={errors[id]}
      />
    </FieldTip>
  );

  return (
    <Card className={styles.card}>
      <CardBody className={styles.body}>
        <section className={styles.addInstance} aria-labelledby="add-instance-title">
          <header className={styles.header}>
            <div className={styles.heading}>
              <h2 id="add-instance-title" className={styles.title}>
                Add Instance
              </h2>
              <p className={styles.subtitle}>Define one instance to add to this portfolio.</p>
            </div>
            {/* Legacy's help button (ActionButtons.vue:22-34), drawn as the
                design's still ⓘ rather than legacy's blinking "?" — never
                disabled, shown only when the page passes onHelp (permission-gated).
                Tooltip side="left": the button sits at the page's right edge, so
                a centred bubble (bottom/top) would hang past it and give the whole
                page a sideways scrollbar. To the left it stays over the card. */}
            {onHelp ? (
              <Tooltip label={HELP_LABEL} side="left">
                <button
                  type="button"
                  className={styles.help}
                  onClick={onHelp}
                  aria-label={HELP_LABEL}
                >
                  <InfoIcon />
                </button>
              </Tooltip>
            ) : null}
          </header>

          <fieldset className={styles.group} disabled={disabled}>
            <legend className={styles.groupTitle}>Generic Metadata</legend>
            <div className={styles.grid}>
              <FieldTip field={FIELD.REGION}>
                <Dropdown
                  id={domId(FIELD.REGION)}
                  label="Region"
                  required
                  searchable
                  value={form[FIELD.REGION]}
                  onChange={changeRegion}
                  options={regions}
                  loading={regionsLoading}
                  placeholder="Select region"
                  // A field error ("Required") first; otherwise a failed load.
                  error={errors[FIELD.REGION] ?? regionsError}
                />
              </FieldTip>
              <FieldTip field={FIELD.INSTANCE_TYPE}>
                <Dropdown
                  id={domId(FIELD.INSTANCE_TYPE)}
                  label="Instance Type"
                  required
                  searchable
                  value={form[FIELD.INSTANCE_TYPE]}
                  onChange={(v) => set(FIELD.INSTANCE_TYPE, v)}
                  options={instanceTypes}
                  loading={instanceTypesLoading}
                  // Instance types depend on the region, so pick that first.
                  disabled={!form[FIELD.REGION]}
                  placeholder="Select instance type"
                  error={errors[FIELD.INSTANCE_TYPE] ?? instanceTypesError}
                />
              </FieldTip>
              <FieldTip field={FIELD.OS}>
                <Dropdown
                  id={domId(FIELD.OS)}
                  label="Operating System"
                  value={form[FIELD.OS]}
                  onChange={(v) => set(FIELD.OS, v)}
                  options={OS_OPTIONS}
                />
              </FieldTip>
              <FieldTip field={FIELD.UUID}>
                <TextField
                  id={domId(FIELD.UUID)}
                  label="UUID / Instance Name"
                  placeholder="Enter UUID / Instance Name"
                  value={form[FIELD.UUID]}
                  onChange={(e) => set(FIELD.UUID, e.target.value)}
                  autoComplete="off"
                />
              </FieldTip>
              <Dropdown
                id={domId(FIELD.PRICING_MODEL)}
                label="Pricing Model"
                required
                value={form[FIELD.PRICING_MODEL]}
                onChange={(v) => set(FIELD.PRICING_MODEL, v)}
                options={PRICING_MODEL_OPTIONS}
                placeholder="Select pricing model"
                error={errors[FIELD.PRICING_MODEL]}
              />
              {flex ? (
                <>
                  <FieldTip field={FIELD.OCPU}>
                    <NumberField
                      id={domId(FIELD.OCPU)}
                      label="OCPU"
                      required
                      placeholder="Enter OCPU"
                      value={form[FIELD.OCPU]}
                      onChange={(e) => set(FIELD.OCPU, e.target.value)}
                      min={0}
                      error={errors[FIELD.OCPU]}
                    />
                  </FieldTip>
                  <FieldTip field={FIELD.MEMORY}>
                    <NumberField
                      id={domId(FIELD.MEMORY)}
                      label="Memory"
                      required
                      placeholder="Enter memory"
                      value={form[FIELD.MEMORY]}
                      onChange={(e) => set(FIELD.MEMORY, e.target.value)}
                      min={0}
                      error={errors[FIELD.MEMORY]}
                    />
                  </FieldTip>
                </>
              ) : null}
            </div>
          </fieldset>

          <fieldset className={styles.group} disabled={disabled}>
            <legend className={styles.groupTitle}>Consumption Data</legend>
            <div className={styles.grid}>
              {CONSUMPTION_FIELDS.map(numberField)}
              <div className={styles.addCell}>
                <Button
                  variant="primary"
                  className={styles.addButton}
                  startIcon={<PlusIcon />}
                  onClick={add}
                  loading={adding}
                  disabled={disabled || adding}
                >
                  Add instance
                </Button>
                {/* Legacy's order: Add, then Find and Replace (ActionButtons.vue:4-21).
                    Labelled here rather than icon-only, to match the Add button. */}
                {onFindReplace ? (
                  <Button
                    variant="secondary-solid"
                    className={styles.addButton}
                    startIcon={<FindReplaceIcon />}
                    onClick={onFindReplace}
                    disabled={Boolean(findReplaceBlockedBy)}
                    title={findReplaceBlockedBy ?? undefined}
                  >
                    Find and Replace
                  </Button>
                ) : null}
              </div>
            </div>
          </fieldset>
        </section>
      </CardBody>
    </Card>
  );
}

export default AddInstance;
